import { useState, useEffect, useCallback, useMemo } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  Percent,
  Plus,
  Pencil,
  Trash2,
  RefreshCw,
  Check,
  X,
  Loader2,
  Calendar,
  Building2,
  MoreHorizontal,
  Infinity,
  Clock,
  AlertCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SearchInput } from "@/components/ui/search-input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { SortableTableHead } from "@/components/ui/sortable-table-head";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Pagination } from "@/components/ui/pagination";
import { toast } from "sonner";
import { discountErrorMessage } from "@/lib/discountErrors";
import { useAuth } from "@/contexts/AuthContext";
import { usePortalData } from "@/components/portal/PortalDataProvider";
import {
  platformApi,
  OrganisationDiscount,
  OrganisationDiscountUpdateRequest,
  DiscountType,
  Organisation,
  PlatformService,
  ServiceFeature,
} from "@/services/api/platform";
import {
  usePaginatedData,
  PaginatedResponse,
} from "@/hooks/usePaginatedData";

// ---------------------------------------------------------------------------
// Constants & helpers
// ---------------------------------------------------------------------------

const DURATION_OPTIONS = [
  { value: "forever", label: "Forever (No End Date)" },
  { value: "1m", label: "1 Month" },
  { value: "3m", label: "3 Months" },
  { value: "6m", label: "6 Months" },
  { value: "12m", label: "12 Months" },
  { value: "custom", label: "Custom Date" },
];

const DISCOUNT_TYPES: { value: DiscountType; label: string }[] = [
  { value: "PROMOTIONAL", label: "Promotional" },
  { value: "NGO", label: "NGO / Non-Profit" },
  { value: "PARTNER", label: "Partner" },
  { value: "EARLY_ADOPTER", label: "Early Adopter" },
  { value: "ENTERPRISE", label: "Enterprise" },
  { value: "CUSTOM", label: "Custom" },
];

function calculateEndDate(
  duration: string,
  startDate: string,
): string | null {
  if (duration === "forever") return null;
  if (duration === "custom") return null;

  const start = new Date(startDate);
  const months = parseInt(duration.replace("m", ""));
  const targetDate = new Date(start);
  targetDate.setMonth(targetDate.getMonth() + months);
  const lastDayOfMonth = new Date(
    targetDate.getFullYear(),
    targetDate.getMonth(),
    0,
  );
  return lastDayOfMonth.toISOString().split("T")[0];
}

function formatDate(dateString: string) {
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

type DiscountScope = "organisation" | "service" | "feature";

interface DiscountFormData {
  discount_percent: string;
  discount_type: DiscountType;
  valid_from: string;
  duration: string;
  custom_valid_to: string;
  scope: DiscountScope;
  service_id: string | null;
  service_feature_id: string | null;
  reason: string;
  is_active: boolean;
}

const DEFAULT_FORM: DiscountFormData = {
  discount_percent: "10",
  discount_type: "PROMOTIONAL",
  valid_from: new Date().toISOString().split("T")[0],
  duration: "forever",
  custom_valid_to: "",
  scope: "organisation",
  service_id: null,
  service_feature_id: null,
  reason: "",
  is_active: true,
};

export const Route = createFileRoute("/portal/platform/discounts")({
  component: DiscountsPage,
});

// ---------------------------------------------------------------------------
// Status / type badges
// ---------------------------------------------------------------------------

function getStatusBadge(discount: OrganisationDiscount) {
  if (!discount.is_active) {
    return (
      <Badge variant="secondary">
        <X className="h-3 w-3 mr-1" />
        Inactive
      </Badge>
    );
  }
  if (discount.is_expired) {
    return (
      <Badge className="bg-gray-100 text-gray-700">
        <Calendar className="h-3 w-3 mr-1" />
        Expired
      </Badge>
    );
  }
  if (discount.is_current) {
    return (
      <Badge variant="success">
        <Check className="h-3 w-3 mr-1" />
        Active
      </Badge>
    );
  }
  return (
    <Badge variant="default">
      <Clock className="h-3 w-3 mr-1" />
      Upcoming
    </Badge>
  );
}

function getTypeBadge(type: DiscountType) {
  const typeInfo = DISCOUNT_TYPES.find((t) => t.value === type);
  const colors: Record<DiscountType, string> = {
    PROMOTIONAL: "bg-purple-100 text-purple-700",
    NGO: "bg-success/20 text-success",
    PARTNER: "bg-primary/10 text-primary",
    EARLY_ADOPTER: "bg-orange-100 text-orange-700",
    ENTERPRISE: "bg-indigo-100 text-indigo-700",
    CUSTOM: "bg-gray-100 text-gray-700",
  };
  return <Badge className={colors[type]}>{typeInfo?.label || type}</Badge>;
}

// ---------------------------------------------------------------------------
// Discount form (shared between create & edit)
// ---------------------------------------------------------------------------

const SCOPE_OPTIONS: { value: DiscountScope; label: string; description: string }[] = [
  { value: "organisation", label: "Organisation-wide", description: "Applies to all services" },
  { value: "service", label: "Service", description: "Applies to a specific service" },
  { value: "feature", label: "Feature", description: "Applies to a specific feature within a service" },
];

interface DiscountFormProps {
  isCreate: boolean;
  formData: DiscountFormData;
  setFormData: React.Dispatch<React.SetStateAction<DiscountFormData>>;
  selectedOrgName: string;
  services: PlatformService[];
  token: string | null;
  /** Earliest Valid From a billed month allows (cp #844); create only. */
  validFromMin?: string | null;
}

function DiscountForm({
  isCreate,
  formData,
  setFormData,
  selectedOrgName,
  services,
  token,
  validFromMin,
}: DiscountFormProps) {
  const validFromBelowMin =
    isCreate && !!validFromMin && !!formData.valid_from && formData.valid_from < validFromMin;

  const [features, setFeatures] = useState<ServiceFeature[]>([]);
  const [loadingScope, setLoadingScope] = useState(false);

  // Load features when service changes and scope requires it
  useEffect(() => {
    if (!token || !formData.service_id) {
      setFeatures([]);
      return;
    }

    if (formData.scope !== "feature") return;

    setLoadingScope(true);
    platformApi
      .listServiceFeatures(token, formData.service_id)
      .then((data) => setFeatures(data.features || []))
      .catch(() => setFeatures([]))
      .finally(() => setLoadingScope(false));
  }, [token, formData.service_id, formData.scope]);

  function handleScopeChange(scope: DiscountScope) {
    setFormData((prev) => ({
      ...prev,
      scope,
      service_id: scope === "organisation" ? null : prev.service_id,
      service_feature_id: null,
    }));
  }

  function handleServiceChange(value: string) {
    setFormData((prev) => ({
      ...prev,
      service_id: value === "all" ? null : value,
      service_feature_id: null,
    }));
  }

  return (
    <div className="space-y-4 py-4">
      {isCreate && (
        <div className="space-y-2">
          <Label>Organisation</Label>
          <div className="flex items-center gap-2 rounded-md border border-input bg-muted/50 px-3 py-2 text-sm">
            <Building2 className="h-4 w-4 text-muted-foreground" />
            <span className="font-medium">{selectedOrgName}</span>
          </div>
          <p className="text-xs text-muted-foreground">
            Using organisation from superadmin context
          </p>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Discount Percent</Label>
          <div className="relative">
            <Input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={formData.discount_percent}
              onChange={(e) => {
                const value = e.target.value;
                if (value === "" || /^\d*\.?\d*$/.test(value)) {
                  setFormData((prev) => ({
                    ...prev,
                    discount_percent: value,
                  }));
                }
              }}
              placeholder="10"
              className="pr-8"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
              %
            </span>
          </div>
          <p className="text-xs text-muted-foreground">Enter 1-100</p>
        </div>
        <div className="space-y-2">
          <Label>Discount Type</Label>
          <Select
            value={formData.discount_type}
            onValueChange={(value) =>
              setFormData((prev) => ({
                ...prev,
                discount_type: value as DiscountType,
              }))
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DISCOUNT_TYPES.map((type) => (
                <SelectItem key={type.value} value={type.value}>
                  {type.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Scope selector */}
      <div className="space-y-2">
        <Label>Discount Scope</Label>
        <Select
          value={formData.scope}
          onValueChange={(value) => handleScopeChange(value as DiscountScope)}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SCOPE_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">
          {SCOPE_OPTIONS.find((o) => o.value === formData.scope)?.description}
        </p>
      </div>

      {/* Service selector (shown for service/item/feature scopes) */}
      {formData.scope !== "organisation" && (
        <div className="space-y-2">
          <Label>Service</Label>
          <Select
            value={formData.service_id || "all"}
            onValueChange={handleServiceChange}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select a service" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Select a service...</SelectItem>
              {services
                .filter((s) => s.is_active)
                .sort((a, b) =>
                  a.display_name.localeCompare(b.display_name),
                )
                .map((service) => (
                  <SelectItem key={service.id} value={service.id}>
                    {service.display_name}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Feature selector */}
      {formData.scope === "feature" && formData.service_id && (
        <div className="space-y-2">
          <Label>Feature</Label>
          {loadingScope ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading features...
            </div>
          ) : features.length === 0 ? (
            <p className="text-sm text-muted-foreground py-2">
              No features found for this service
            </p>
          ) : (
            <Select
              value={formData.service_feature_id || "none"}
              onValueChange={(value) =>
                setFormData((prev) => ({
                  ...prev,
                  service_feature_id: value === "none" ? null : value,
                }))
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Select feature" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Select feature...</SelectItem>
                {features
                  .filter((f) => f.is_active)
                  .map((f) => (
                    <SelectItem key={f.id} value={f.id}>
                      {f.display_name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          )}
        </div>
      )}

      <div className="space-y-2">
        <Label>Valid From</Label>
        <Input
          type="date"
          data-testid="discount-valid-from"
          value={formData.valid_from}
          min={isCreate ? (validFromMin ?? undefined) : undefined}
          aria-invalid={validFromBelowMin || undefined}
          onChange={(e) =>
            setFormData((prev) => ({ ...prev, valid_from: e.target.value }))
          }
        />
        {isCreate && validFromMin && (
          <p className="text-xs text-muted-foreground">
            Earliest allowed: {validFromMin}. A month already invoiced cannot be discounted.
          </p>
        )}
        {validFromBelowMin && (
          <p
            data-testid="discount-valid-from-below-min"
            role="alert"
            className="flex items-center gap-1 text-xs text-destructive"
          >
            <AlertCircle className="h-3 w-3 shrink-0" />
            {formData.valid_from} is before {validFromMin}, which falls in a billed month.
          </p>
        )}
      </div>

      <div className="space-y-2">
        <Label>Duration</Label>
        <Select
          value={formData.duration}
          onValueChange={(value) =>
            setFormData((prev) => ({
              ...prev,
              duration: value,
              custom_valid_to: "",
            }))
          }
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {DURATION_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                <span className="flex items-center gap-2">
                  {opt.value === "forever" && (
                    <Infinity className="h-3 w-3" />
                  )}
                  {opt.label}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {formData.duration === "forever" && (
          <p className="text-xs text-muted-foreground">
            Discount will never expire
          </p>
        )}
      </div>

      {formData.duration === "custom" && (
        <div className="space-y-2">
          <Label>Valid To</Label>
          <Input
            type="date"
            value={formData.custom_valid_to}
            min={formData.valid_from}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                custom_valid_to: e.target.value,
              }))
            }
          />
        </div>
      )}

      <div className="space-y-2">
        <Label>Reason / Notes</Label>
        <Textarea
          value={formData.reason || ""}
          onChange={(e) =>
            setFormData((prev) => ({ ...prev, reason: e.target.value }))
          }
          placeholder="e.g., Partner discount for integration work"
          rows={3}
        />
      </div>

      {/* EDIT ONLY — this switch is the RETIRE toggle, and a discount cannot be
          born retired. cp #550 made `is_active` a derived view of
          `RetiredAtUtc`: on a request False means "retire" and True means
          "un-retire", and `create_discount` refuses `is_active=False` with a
          409, because retirement records the instant it happened and a row that
          was never live has no such instant
          (`schemas/billing.py:758` on cp `origin/main`). Rendering it on CREATE
          offered an admin a control whose only non-default position was a
          guaranteed 409. */}
      {!isCreate && (
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label>Active</Label>
            <p className="text-sm text-muted-foreground">
              Turning this off retires the discount from today; turning it back
              on un-retires it
            </p>
          </div>
          <Switch
            checked={formData.is_active}
            onCheckedChange={(checked) =>
              setFormData((prev) => ({ ...prev, is_active: checked }))
            }
          />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

function DiscountsPage() {
  const { token, user } = useAuth();
  const { selectedOrganisation } = usePortalData();
  const isPlatformAdmin = user?.is_admin === true;
  const [organisations, setOrganisations] = useState<Organisation[]>([]);
  const [services, setServices] = useState<PlatformService[]>([]);
  const [showInactive, setShowInactive] = useState(false);
  const [showExpired, setShowExpired] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [orgFilter, setOrgFilter] = useState<string>("all");
  const [sortBy, setSortBy] = useState("created_at");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingDiscount, setEditingDiscount] =
    useState<OrganisationDiscount | null>(null);
  const [deactivateDiscount, setDeactivateTarget] =
    useState<OrganisationDiscount | null>(null);
  const [deleteTarget, setDeleteTarget] =
    useState<OrganisationDiscount | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState<DiscountFormData>({
    ...DEFAULT_FORM,
  });
  // Earliest Valid From for a new discount (cp #844). The CP refuses an
  // earlier one; this only keeps the picker from offering it.
  const [validFromMin, setValidFromMin] = useState<string | null>(null);
  const createOrgId = selectedOrganisation?.organisationId;

  useEffect(() => {
    setValidFromMin(null);
    if (!showCreateDialog || !token || !createOrgId || !isPlatformAdmin) return;
    let cancelled = false;
    platformApi
      .getBillingFloor(token, createOrgId)
      .then((floor) => {
        if (!cancelled) setValidFromMin(floor.floor);
      })
      .catch((error) => console.error("Failed to load billing floor:", error));
    return () => {
      cancelled = true;
    };
  }, [showCreateDialog, token, createOrgId, isPlatformAdmin]);

  const selectedOrgName = useMemo(() => {
    return (
      selectedOrganisation?.data?.CompanyName ||
      selectedOrganisation?.organisationName ||
      "Selected Organisation"
    );
  }, [selectedOrganisation]);

  // Sync org filter when header org switcher changes
  useEffect(() => {
    if (selectedOrganisation?.organisationId) {
      setOrgFilter(selectedOrganisation.organisationId);
    }
  }, [selectedOrganisation?.organisationId]);

  // Server-side pagination
  const fetchDiscounts = useCallback(
    async (
      page: number,
      pageSize: number,
    ): Promise<PaginatedResponse<OrganisationDiscount>> => {
      if (!token) {
        return { data: [], totalCount: 0, page, pageSize };
      }
      try {
        const response = await platformApi.listAllDiscounts(token, {
          organisation_id: orgFilter !== "all" ? orgFilter : undefined,
          include_inactive: showInactive,
          include_expired: showExpired,
          search: searchTerm || undefined,
          offset: (page - 1) * pageSize,
          limit: pageSize,
          sort_by: sortBy,
          sort_order: sortOrder,
        });
        return {
          data: response.items || [],
          totalCount: response.total,
          page,
          pageSize,
        };
      } catch (error) {
        console.error("Failed to load discounts:", error);
        toast.error("Failed to load discounts");
        return { data: [], totalCount: 0, page, pageSize };
      }
    },
    [
      token,
      orgFilter,
      showInactive,
      showExpired,
      searchTerm,
      sortBy,
      sortOrder,
    ],
  );

  const {
    data: discounts,
    isLoading,
    pagination,
    goToPage,
    nextPage,
    prevPage,
    firstPage,
    lastPage,
    setPageSize,
    refresh,
    canGoNext,
    canGoPrev,
    startIndex,
    endIndex,
  } = usePaginatedData<OrganisationDiscount>({
    fetchFn: fetchDiscounts,
    initialPageSize: 25,
    dependencies: [
      token,
      orgFilter,
      showInactive,
      showExpired,
      searchTerm,
      sortBy,
      sortOrder,
    ],
  });

  // Load supporting data
  useEffect(() => {
    if (!token) return;
    Promise.all([
      platformApi.listOrganisations(token, { limit: 100 }),
      platformApi.listPlatformServices(token, true),
    ])
      .then(([orgsData, servicesData]) => {
        setOrganisations(orgsData.items || []);
        setServices(servicesData);
      })
      .catch((err) => {
        console.error("Failed to load supporting data:", err);
      });
  }, [token]);

  function handleSort(column: string) {
    if (sortBy === column) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(column);
      setSortOrder("asc");
    }
  }

  function getSortDirection(column: string): "asc" | "desc" | null {
    return sortBy === column ? sortOrder : null;
  }

  function getOrgName(orgId: string): string {
    const org = organisations.find((o) => o.id === orgId);
    return org?.name || "Unknown";
  }

  function getServiceName(serviceId: string | null): string {
    if (!serviceId) return "All Services";
    const service = services.find((s) => s.id === serviceId);
    return service?.display_name || service?.name || "Unknown";
  }

  function getScopeLabel(discount: OrganisationDiscount): string {
    if (discount.service_feature_id) return "Feature";
    if (discount.service_id) return "Service";
    return "Organisation";
  }

  function resetForm() {
    setFormData({ ...DEFAULT_FORM });
  }

  function getValidToFromForm(): string | null {
    if (formData.duration === "forever") return null;
    if (formData.duration === "custom") {
      return formData.custom_valid_to || null;
    }
    return calculateEndDate(formData.duration, formData.valid_from);
  }

  function openEditDialog(discount: OrganisationDiscount) {
    let duration = "forever";
    let customValidTo = "";
    if (discount.valid_to) {
      const validTo = new Date(discount.valid_to);
      const validFrom = new Date(discount.valid_from);
      const monthsDiff =
        (validTo.getFullYear() - validFrom.getFullYear()) * 12 +
        (validTo.getMonth() - validFrom.getMonth());
      if (monthsDiff === 1) duration = "1m";
      else if (monthsDiff === 3) duration = "3m";
      else if (monthsDiff === 6) duration = "6m";
      else if (monthsDiff === 12) duration = "12m";
      else {
        duration = "custom";
        customValidTo = discount.valid_to.split("T")[0];
      }
    }

    // Determine scope from existing data
    let scope: DiscountScope = "organisation";
    if (discount.service_feature_id) scope = "feature";
    else if (discount.service_id) scope = "service";

    setFormData({
      discount_percent: String(discount.discount_percent),
      discount_type: discount.discount_type,
      valid_from: discount.valid_from.split("T")[0],
      duration,
      custom_valid_to: customValidTo,
      scope,
      service_id: discount.service_id,
      service_feature_id: discount.service_feature_id,
      reason: discount.reason || "",
      is_active: discount.is_active,
    });
    setEditingDiscount(discount);
  }

  async function handleCreateDiscount() {
    if (!token || !selectedOrganisation) return;
    const percent = parseFloat(formData.discount_percent);
    if (isNaN(percent) || percent < 1 || percent > 100) {
      toast.error("Discount percent must be between 1 and 100");
      return;
    }
    setIsSaving(true);
    try {
      const validTo = getValidToFromForm();
      await platformApi.createDiscount(token, {
        organisation_id: selectedOrganisation.organisationId,
        discount_percent: percent,
        discount_type: formData.discount_type,
        valid_from: new Date(formData.valid_from).toISOString(),
        valid_to: validTo ? new Date(validTo).toISOString() : null,
        service_id: formData.scope !== "organisation" ? formData.service_id : null,
        service_feature_id: formData.scope === "feature" ? formData.service_feature_id : null,
        reason: formData.reason,
        // `is_active` is DELIBERATELY NOT SENT. The server defaults it to True
        // (`OrganisationDiscountBase`, cp `origin/main`) and refuses False on
        // create with a 409. Sending the form's value made the create dialog a
        // dead end whenever the switch was off; omitting it means the only
        // reachable state is the only accepted one.
      });
      toast.success("Discount created successfully");
      setShowCreateDialog(false);
      resetForm();
      refresh();
    } catch (error) {
      console.error("Failed to create discount:", error);
      // Create refuses with a 409 too — an overlapping discount in the same
      // scope, named with its percent, type and dates. Missed on the first
      // pass, which wrapped only update/deactivate/delete: the same rule
      // applied to one fewer site than it needed.
      toast.error(discountErrorMessage(error, "Failed to create discount"));
    } finally {
      setIsSaving(false);
    }
  }

  async function handleUpdateDiscount() {
    if (!token || !editingDiscount) return;
    const percent = parseFloat(formData.discount_percent);
    if (isNaN(percent) || percent < 1 || percent > 100) {
      toast.error("Discount percent must be between 1 and 100");
      return;
    }
    setIsSaving(true);
    try {
      const validTo = getValidToFromForm();
      const updateData: OrganisationDiscountUpdateRequest = {
        discount_percent: percent,
        discount_type: formData.discount_type,
        valid_from: new Date(formData.valid_from).toISOString(),
        valid_to: validTo ? new Date(validTo).toISOString() : null,
        service_id: formData.scope !== "organisation" ? formData.service_id : null,
        service_feature_id: formData.scope === "feature" ? formData.service_feature_id : null,
        reason: formData.reason,
        is_active: formData.is_active,
      };
      await platformApi.updateDiscount(
        token,
        editingDiscount.id,
        updateData,
      );
      toast.success("Discount updated successfully");
      setEditingDiscount(null);
      resetForm();
      refresh();
    } catch (error) {
      console.error("Failed to update discount:", error);
      toast.error(discountErrorMessage(error, "Failed to update discount"));
    } finally {
      setIsSaving(false);
    }
  }

  async function handleDeactivate(discount: OrganisationDiscount) {
    if (!token) return;
    try {
      await platformApi.deactivateDiscount(token, discount.id);
      toast.success("Discount deactivated");
      setDeactivateTarget(null);
      refresh();
    } catch (error) {
      console.error("Failed to deactivate discount:", error);
      toast.error(discountErrorMessage(error, "Failed to deactivate discount"));
    }
  }

  async function handleDelete(discount: OrganisationDiscount) {
    if (!token) return;
    try {
      await platformApi.deleteDiscountPermanently(token, discount.id);
      toast.success("Discount permanently deleted");
      setDeleteTarget(null);
      refresh();
    } catch (error) {
      console.error("Failed to delete discount:", error);
      toast.error(discountErrorMessage(error, "Failed to delete discount"));
    }
  }

  const stats = useMemo(
    () => ({
      total: pagination.totalCount,
      active: discounts.filter((d) => d.is_current).length,
      expired: discounts.filter((d) => d.is_expired).length,
      upcoming: discounts.filter(
        (d) => !d.is_current && !d.is_expired && d.is_active,
      ).length,
    }),
    [discounts, pagination.totalCount],
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-slate-900">Discounts</h1>
          <p className="mt-2 text-lg text-slate-600">
            Manage organisation discounts — promotional, NGO, partner, and more
          </p>
        </div>
        <Button
          onClick={() => setShowCreateDialog(true)}
          className="rounded-full"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Discount
        </Button>
      </div>

      {/* Content panel */}
      <div className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm space-y-6">
        {/* Controls bar */}
        <div className="flex flex-wrap items-center gap-3">
          <SearchInput
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onClear={() => setSearchTerm("")}
            placeholder="Search by organisation or reason..."
            containerClassName="flex-1 min-w-[200px]"
          />
          <Select value={orgFilter} onValueChange={setOrgFilter}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="All Organisations" />
            </SelectTrigger>
            <SelectContent className="max-h-[300px]">
              <SelectItem value="all">All Organisations</SelectItem>
              {organisations.map((org) => (
                <SelectItem key={org.id} value={org.id}>
                  {org.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex items-center gap-2">
            <Switch
              id="show-inactive"
              checked={showInactive}
              onCheckedChange={setShowInactive}
            />
            <Label htmlFor="show-inactive" className="text-sm text-slate-600">
              Inactive
            </Label>
          </div>
          <div className="flex items-center gap-2">
            <Switch
              id="show-expired"
              checked={showExpired}
              onCheckedChange={setShowExpired}
            />
            <Label htmlFor="show-expired" className="text-sm text-slate-600">
              Expired
            </Label>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => refresh()}
            disabled={isLoading}
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Refresh
          </Button>
        </div>

        {/* Stats cards */}
        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Total Discounts
              </CardTitle>
              <Percent className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.total}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Currently Active
              </CardTitle>
              <Check className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">
                {stats.active}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Upcoming</CardTitle>
              <Clock className="h-4 w-4 text-blue-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-600">
                {stats.upcoming}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Expired</CardTitle>
              <Calendar className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-gray-600">
                {stats.expired}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Table */}
        {isLoading && discounts.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : discounts.length === 0 ? (
          <div className="text-center py-12">
            <Percent className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">
              {searchTerm
                ? "No discounts match your search"
                : "No discounts found"}
            </h3>
            <p className="text-muted-foreground mb-4">
              {searchTerm
                ? "Try a different search term."
                : "Create a discount to get started."}
            </p>
            {!searchTerm && (
              <Button onClick={() => setShowCreateDialog(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Add Discount
              </Button>
            )}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <SortableTableHead
                  direction={getSortDirection("organisation_id")}
                  onSort={() => handleSort("organisation_id")}
                >
                  Organisation
                </SortableTableHead>
                <SortableTableHead
                  direction={getSortDirection("discount_percent")}
                  onSort={() => handleSort("discount_percent")}
                >
                  Discount
                </SortableTableHead>
                <SortableTableHead
                  direction={getSortDirection("discount_type")}
                  onSort={() => handleSort("discount_type")}
                >
                  Type
                </SortableTableHead>
                <SortableTableHead
                  direction={getSortDirection("service_id")}
                  onSort={() => handleSort("service_id")}
                >
                  Scope
                </SortableTableHead>
                <SortableTableHead
                  direction={getSortDirection("valid_from")}
                  onSort={() => handleSort("valid_from")}
                >
                  Valid From
                </SortableTableHead>
                <SortableTableHead
                  direction={getSortDirection("valid_to")}
                  onSort={() => handleSort("valid_to")}
                >
                  Valid To
                </SortableTableHead>
                <SortableTableHead
                  direction={null}
                  onSort={() => {}}
                  disabled
                >
                  Status
                </SortableTableHead>
                <SortableTableHead
                  direction={null}
                  onSort={() => {}}
                  disabled
                  className="w-[50px]"
                >
                  {" "}
                </SortableTableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {discounts.map((discount) => (
                <TableRow key={discount.id}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                      <span className="font-medium">
                        {getOrgName(discount.organisation_id)}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="text-lg font-bold text-green-600">
                      {discount.discount_percent}%
                    </span>
                  </TableCell>
                  <TableCell>{getTypeBadge(discount.discount_type)}</TableCell>
                  <TableCell className="text-sm">
                    <div>
                      <Badge variant="outline" className="text-xs">
                        {getScopeLabel(discount)}
                      </Badge>
                      {discount.service_id && (
                        <p className="text-muted-foreground mt-0.5 text-xs">
                          {getServiceName(discount.service_id)}
                        </p>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm">
                    {formatDate(discount.valid_from)}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {discount.valid_to
                      ? formatDate(discount.valid_to)
                      : "No end date"}
                  </TableCell>
                  <TableCell>{getStatusBadge(discount)}</TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                        >
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={() => openEditDialog(discount)}
                        >
                          <Pencil className="mr-2 h-4 w-4" />
                          Edit
                        </DropdownMenuItem>
                        {discount.is_active && (
                          <DropdownMenuItem
                            onClick={() => setDeactivateTarget(discount)}
                          >
                            <X className="mr-2 h-4 w-4" />
                            Deactivate
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => setDeleteTarget(discount)}
                          className="text-destructive focus:text-destructive"
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          Delete Permanently
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {/* Pagination */}
        {pagination.totalCount > 0 && (
          <div className="mt-4 border-t pt-4">
            <Pagination
              page={pagination.page}
              totalPages={pagination.totalPages}
              totalCount={pagination.totalCount}
              pageSize={pagination.pageSize}
              pageSizeOptions={[10, 25, 50, 100]}
              startIndex={startIndex}
              endIndex={endIndex}
              canGoPrev={canGoPrev}
              canGoNext={canGoNext}
              onPageChange={goToPage}
              onPageSizeChange={setPageSize}
              onFirstPage={firstPage}
              onLastPage={lastPage}
              onPrevPage={prevPage}
              onNextPage={nextPage}
            />
          </div>
        )}
      </div>

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Discount</DialogTitle>
            <DialogDescription>
              Add a new discount for an organisation
            </DialogDescription>
          </DialogHeader>
          <DiscountForm
            isCreate={true}
            formData={formData}
            setFormData={setFormData}
            selectedOrgName={selectedOrgName}
            services={services}
            token={token}
            validFromMin={validFromMin}
          />
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowCreateDialog(false);
                resetForm();
              }}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateDiscount}
              disabled={isSaving || !selectedOrganisation}
            >
              {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Create Discount
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog
        open={editingDiscount !== null}
        onOpenChange={(open) => {
          if (!open) {
            setEditingDiscount(null);
            resetForm();
          }
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Discount</DialogTitle>
            <DialogDescription>
              Update discount for{" "}
              {editingDiscount && getOrgName(editingDiscount.organisation_id)}
            </DialogDescription>
          </DialogHeader>
          <DiscountForm
            isCreate={false}
            formData={formData}
            setFormData={setFormData}
            selectedOrgName={selectedOrgName}
            services={services}
            token={token}
          />
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setEditingDiscount(null);
                resetForm();
              }}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button onClick={handleUpdateDiscount} disabled={isSaving}>
              {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Deactivate Confirmation */}
      <AlertDialog
        open={deactivateDiscount !== null}
        onOpenChange={(open) => !open && setDeactivateTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deactivate Discount?</AlertDialogTitle>
            <AlertDialogDescription>
              This will deactivate the {deactivateDiscount?.discount_percent}%
              discount for{" "}
              {deactivateDiscount &&
                getOrgName(deactivateDiscount.organisation_id)}
              . The discount can be reactivated later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() =>
                deactivateDiscount && handleDeactivate(deactivateDiscount)
              }
            >
              Deactivate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Permanently Confirmation */}
      <AlertDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Discount Permanently?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the {deleteTarget?.discount_percent}%
              discount for{" "}
              {deleteTarget && getOrgName(deleteTarget.organisation_id)}. This
              action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteTarget && handleDelete(deleteTarget)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete Permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
